import React, { useState, useEffect, useRef } from "react";
import HeaderLayout from "../common/HeaderLayout";
import { useAuth } from "./Authcontext";
import { toast, ToastContainer } from "react-toastify";

const API_URL = "https://api.rangpursadhima.cloud/api";

const PRODUCTS = {
  combo:  { label: "કોમ્બો (4 નંગ)", price: 900,  emoji: "🎁" },
  single: { label: "સિંગલ પ્રોડક્ટ",  price: 250,  emoji: "📦" },
};

// ─── Bill Print Component ────────────────────────────────────────────────────
function BillPrint({ sale, onClose }) {
  const printRef = useRef();

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const win = window.open("", "_blank", "width=400,height=600");
    win.document.write(`
      <html><head><title>Bill</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; max-width: 300px; margin: auto; font-size: 13px; }
        .center { text-align: center; }
        .bold { font-weight: bold; }
        .line { border-top: 1px dashed #000; margin: 8px 0; }
        .row { display: flex; justify-content: space-between; margin: 4px 0; }
        .title { font-size: 18px; font-weight: bold; color: #B40000; }
        .bill-no { font-size: 11px; color: #555; }
        .total { font-size: 16px; font-weight: bold; }
        .badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: bold; }
        .cash { background: #d1fae5; color: #065f46; }
        .online { background: #dbeafe; color: #1e40af; }
        .footer { font-size: 10px; color: #888; text-align: center; margin-top: 12px; }
      </style>
      </head><body>${content}</body></html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const date = new Date(sale.created_at);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        {/* Bill preview */}
        <div ref={printRef} className="p-6">
          <div className="text-center mb-4">
            <div className="text-xl font-bold text-red-700">🕌 Rangpur Sadhima</div>
            <div className="text-xs text-gray-500">Sales Receipt</div>
            <div className="text-xs text-gray-400 mt-1">
              {date.toLocaleDateString("gu-IN", { day: "numeric", month: "long", year: "numeric" })}
              &nbsp;|&nbsp;
              {date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}
            </div>
          </div>

          <div className="border-t border-dashed border-gray-300 my-3" />

          <div className="text-xs text-gray-500 text-center font-mono mb-3">{sale.bill_no}</div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">ઉત્પાદન</span>
              <span className="font-semibold">{PRODUCTS[sale.product_type]?.label || sale.product_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">જથ્થો</span>
              <span className="font-semibold">{sale.quantity} × ₹{sale.price_per_unit}</span>
            </div>
            {sale.customer_name && (
              <div className="flex justify-between">
                <span className="text-gray-600">ગ્રાહક</span>
                <span className="font-semibold">{sale.customer_name}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">ચુકવણી</span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${sale.payment_mode === "cash" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}`}>
                {sale.payment_mode === "cash" ? "💵 રોકડ" : "📱 ઓનલાઈન"}
              </span>
            </div>
          </div>

          <div className="border-t border-dashed border-gray-300 my-3" />

          <div className="flex justify-between items-center">
            <span className="text-base font-bold text-gray-700">કુલ રકમ</span>
            <span className="text-2xl font-bold text-red-700">₹{sale.total_amount}</span>
          </div>

          <div className="border-t border-dashed border-gray-300 my-3" />

          <div className="text-center text-xs text-gray-400">
            <div>Sold by: {sale.sold_by_name}</div>
            <div className="mt-1">આભાર 🙏</div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={handlePrint}
            className="flex-1 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 flex items-center justify-center gap-2"
          >
            🖨️ પ્રિન્ટ કરો
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50"
          >
            બંધ
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main SalesPage ──────────────────────────────────────────────────────────
export default function SalesPage() {
  const { user, role, token } = useAuth();

  // Form state
  const [productType, setProductType] = useState("combo");
  const [quantity, setQuantity] = useState(1);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [customerName, setCustomerName] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Bill modal
  const [printBill, setPrintBill] = useState(null);

  // Today's sales
  const [todaySales, setTodaySales] = useState([]);
  const [todaySummary, setTodaySummary] = useState({ total: 0, cash: 0, online: 0, bills: 0 });
  const [loadingSales, setLoadingSales] = useState(false);

  // Delete confirm
  const [deletingId, setDeletingId] = useState(null);

  const isSuperAdmin = role === "SUPERADMIN";
  const soldById   = user?.id;
  const soldByName = user?.name || "Unknown";

  const totalAmount = PRODUCTS[productType].price * quantity;

  // ── Fetch today's sales ────────────────────────────────────────────────────
  const fetchTodaySales = async () => {
    setLoadingSales(true);
    try {
      const params = isSuperAdmin ? "" : `?sold_by_id=${soldById}`;
      const res = await fetch(`${API_URL}/sales/today${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTodaySales(data);

        // Calculate summary
        const summary = data.reduce(
          (acc, s) => {
            acc.bills++;
            acc.total += Number(s.total_amount);
            if (s.payment_mode === "cash") acc.cash += Number(s.total_amount);
            else acc.online += Number(s.total_amount);
            return acc;
          },
          { total: 0, cash: 0, online: 0, bills: 0 }
        );
        setTodaySummary(summary);
      }
    } catch (err) {
      console.error("Error fetching today's sales:", err);
    } finally {
      setLoadingSales(false);
    }
  };

  useEffect(() => {
    if (soldById) fetchTodaySales();
  }, [soldById]);

  // ── Submit sale ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!soldById) return toast.error("User info not found");
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/sales`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          product_type: productType,
          quantity,
          payment_mode: paymentMode,
          customer_name: customerName || null,
          notes: notes || null,
          sold_by_id: soldById,
          sold_by_name: soldByName,
          sold_by_role: role,
        }),
      });

      if (res.ok) {
        const sale = await res.json();
        toast.success(`✅ બિલ ${sale.bill_no} બન્યું!`);

        // Reset form
        setQuantity(1);
        setCustomerName("");
        setNotes("");

        // Show print modal
        setPrintBill(sale);

        // Refresh list
        fetchTodaySales();
      } else {
        const err = await res.json();
        toast.error(err.error || "Sale failed");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Delete sale ────────────────────────────────────────────────────────────
  const handleDelete = async (saleId) => {
    if (!window.confirm("આ sale delete કરવી છે?")) return;
    setDeletingId(saleId);
    try {
      const res = await fetch(`${API_URL}/sales/${saleId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          actorId: soldById,
          actorRole: role,
          actorName: soldByName,
        }),
      });

      if (res.ok) {
        toast.success("Sale delete થઈ");
        fetchTodaySales();
      } else {
        const err = await res.json();
        toast.error(err.error || "Delete failed");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <HeaderLayout title="Sales">
      <ToastContainer />

      {printBill && (
        <BillPrint sale={printBill} onClose={() => setPrintBill(null)} />
      )}

      <div className="max-w-5xl mx-auto space-y-6">

        {/* ── New Sale Form ── */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="bg-red-600 px-6 py-4">
            <h2 className="text-xl font-bold text-white">🛒 નવી Sale</h2>
          </div>

          <div className="p-6 space-y-5">

            {/* Product Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">ઉત્પાદન પસંદ કરો</label>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(PRODUCTS).map(([key, prod]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setProductType(key)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      productType === key
                        ? "border-red-500 bg-red-50"
                        : "border-gray-200 hover:border-red-300"
                    }`}
                  >
                    <div className="text-3xl mb-1">{prod.emoji}</div>
                    <div className="font-semibold text-gray-800">{prod.label}</div>
                    <div className="text-lg font-bold text-red-600 mt-1">₹{prod.price}</div>
                    {key === "combo" && (
                      <div className="text-xs text-gray-500 mt-0.5">4 નંગ સાથે</div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">જથ્થો</label>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-xl font-bold text-gray-700 flex items-center justify-center"
                >
                  −
                </button>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 text-center border-2 border-gray-200 rounded-lg py-2 text-lg font-bold focus:outline-none focus:border-red-400"
                />
                <button
                  type="button"
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-xl font-bold text-gray-700 flex items-center justify-center"
                >
                  +
                </button>
                <span className="text-gray-500 text-sm ml-2">
                  = <span className="text-red-600 font-bold text-lg">₹{totalAmount}</span>
                </span>
              </div>
            </div>

            {/* Payment Mode */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">ચુકવણી પ્રકાર</label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setPaymentMode("cash")}
                  className={`p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                    paymentMode === "cash"
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200 hover:border-green-300"
                  }`}
                >
                  <span className="text-2xl">💵</span>
                  <div className="text-left">
                    <div className="font-semibold text-gray-800">રોકડ</div>
                    <div className="text-xs text-gray-500">Cash</div>
                  </div>
                  {paymentMode === "cash" && (
                    <span className="ml-auto text-green-600 text-lg">✓</span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMode("online")}
                  className={`p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                    paymentMode === "online"
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-blue-300"
                  }`}
                >
                  <span className="text-2xl">📱</span>
                  <div className="text-left">
                    <div className="font-semibold text-gray-800">ઓનલાઈન</div>
                    <div className="text-xs text-gray-500">UPI / Online</div>
                  </div>
                  {paymentMode === "online" && (
                    <span className="ml-auto text-blue-600 text-lg">✓</span>
                  )}
                </button>
              </div>
            </div>

            {/* Customer Name (optional) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                ગ્રાહકનું નામ <span className="text-gray-400 font-normal">(વૈકલ્પિક)</span>
              </label>
              <input
                type="text"
                placeholder="ગ્રાહકનું નામ દાખલ કરો..."
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-red-400"
              />
            </div>

            {/* Summary Row */}
            <div className="bg-gray-50 rounded-xl p-4 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{PRODUCTS[productType].label}</span>
                {" × "}{quantity}
                {" — "}
                <span className={`font-semibold ${paymentMode === "cash" ? "text-green-700" : "text-blue-700"}`}>
                  {paymentMode === "cash" ? "💵 રોકડ" : "📱 ઓનલાઈન"}
                </span>
              </div>
              <div className="text-2xl font-bold text-red-600">₹{totalAmount}</div>
            </div>

            {/* Submit */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full py-4 bg-red-600 text-white rounded-xl text-lg font-bold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? "⏳ Sale નોંધાઈ રહી છે..." : "✅ Sale નોંધો & Bill બનાવો"}
            </button>
          </div>
        </div>

        {/* ── Today's Summary Cards ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard label="આજની Bills" value={todaySummary.bills} color="bg-purple-100 text-purple-800" icon="🧾" />
          <SummaryCard label="કુલ વેચાણ" value={`₹${todaySummary.total}`} color="bg-red-100 text-red-800" icon="💰" />
          <SummaryCard label="રોકડ" value={`₹${todaySummary.cash}`} color="bg-green-100 text-green-800" icon="💵" />
          <SummaryCard label="ઓનલાઈન" value={`₹${todaySummary.online}`} color="bg-blue-100 text-blue-800" icon="📱" />
        </div>

        {/* ── Today's Sales Table ── */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="bg-orange-500 px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-bold text-white">
              📋 {isSuperAdmin ? "આજની બધી Sales" : "આજની મારી Sales"}
            </h2>
            <button
              onClick={fetchTodaySales}
              className="text-sm text-white bg-orange-600 hover:bg-orange-700 px-3 py-1 rounded-lg"
            >
              🔄 Refresh
            </button>
          </div>

          {loadingSales ? (
            <div className="py-12 text-center text-gray-500">Loading...</div>
          ) : todaySales.length === 0 ? (
            <div className="py-12 text-center text-gray-400">
              <div className="text-5xl mb-3">🛒</div>
              <p>આજ કોઈ sale નથી</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-gray-600 font-semibold">Bill No</th>
                    <th className="px-4 py-3 text-left text-gray-600 font-semibold">ઉત્પાદન</th>
                    <th className="px-4 py-3 text-center text-gray-600 font-semibold">જથ્થો</th>
                    <th className="px-4 py-3 text-right text-gray-600 font-semibold">રકમ</th>
                    <th className="px-4 py-3 text-center text-gray-600 font-semibold">ચુકવણી</th>
                    {isSuperAdmin && (
                      <th className="px-4 py-3 text-left text-gray-600 font-semibold">Sold By</th>
                    )}
                    <th className="px-4 py-3 text-left text-gray-600 font-semibold">ગ્રાહક</th>
                    <th className="px-4 py-3 text-center text-gray-600 font-semibold">સમય</th>
                    <th className="px-4 py-3 text-center text-gray-600 font-semibold">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {todaySales.map((sale, i) => (
                    <tr key={sale.id} className={`border-b ${i % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-orange-50`}>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{sale.bill_no}</td>
                      <td className="px-4 py-3">
                        <span className="font-medium">
                          {PRODUCTS[sale.product_type]?.emoji} {PRODUCTS[sale.product_type]?.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center font-semibold">{sale.quantity}</td>
                      <td className="px-4 py-3 text-right font-bold text-red-600">₹{sale.total_amount}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                          sale.payment_mode === "cash"
                            ? "bg-green-100 text-green-800"
                            : "bg-blue-100 text-blue-800"
                        }`}>
                          {sale.payment_mode === "cash" ? "💵 રોકડ" : "📱 ઓનલાઈન"}
                        </span>
                      </td>
                      {isSuperAdmin && (
                        <td className="px-4 py-3 text-sm text-gray-700">{sale.sold_by_name}</td>
                      )}
                      <td className="px-4 py-3 text-xs text-gray-500">{sale.customer_name || "—"}</td>
                      <td className="px-4 py-3 text-center text-xs text-gray-500">
                        {new Date(sale.created_at).toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleDelete(sale.id)}
                          disabled={deletingId === sale.id}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded disabled:opacity-40"
                          title="Sale Delete કરો"
                        >
                          {deletingId === sale.id ? "⏳" : "🗑️"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-orange-50 border-t-2 border-orange-200">
                    <td colSpan={isSuperAdmin ? 3 : 2} className="px-4 py-3 font-bold text-gray-700">
                      કુલ ({todaySales.length} bills)
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-red-700 text-base">
                      ₹{todaySummary.total}
                    </td>
                    <td colSpan={isSuperAdmin ? 5 : 4} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </HeaderLayout>
  );
}

function SummaryCard({ label, value, color, icon }) {
  return (
    <div className={`rounded-xl p-4 ${color} shadow-sm`}>
      <div className="text-2xl mb-1">{icon}</div>
      <div className="text-sm font-medium opacity-80">{label}</div>
      <div className="text-xl font-bold mt-1">{value}</div>
    </div>
  );
}